import { useEffect, useRef } from "react";

const expertises = [
  {
    icon: "ri-lightbulb-flash-line",
    title: "Étude de faisabilité \u0026 approche créative",
    description:
      "Analyse du projet, réflexion stratégique, demandes aux autorités, concept, positionnement et développement d'une vision cohérente adaptée au public cible en toute sécurité.",
  },
  {
    icon: "ri-calendar-schedule-line",
    title: "Planification \u0026 coordination",
    description:
      "Gestion des budgets \u0026 devis, implantation du lieu, sourcing fournisseurs, gestion des flux, logistique, coordination générale, recrutement \u0026 gestion des équipes, exposants et partenaires.",
  },
  {
    icon: "ri-restaurant-line",
    title: "Food \u0026 Beverage",
    description:
      "Création de l'offre F&B, élaboration des cartes, gestion des coûts, infrastructures, rentabilité et expérience consommateur.",
  },
  {
    icon: "ri-palette-line",
    title: "Direction créative \u0026 scénographie",
    description:
      "Univers visuel, ambiance, scénographie, animations, identité événementielle et coordination de la communication.",
  },
  {
    icon: "ri-hand-heart-line",
    title: "Réseau \u0026 partenaires de confiance",
    description:
      "Accès à un large réseau de partenaires, fournisseurs, prestataires et intervenants sélectionnés avec soin afin de garantir des collaborations fiables, cohérentes et adaptées à chaque projet.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Gestion opérationnelle",
    description:
      "Présence et coordination sur site durant toute la durée de l'événement afin d'assurer une gestion fluide, réactive et précise de chaque instant.",
  },
  {
    icon: "ri-tools-line",
    title: "Montage \u0026 démontage",
    description:
      "Organisation, structuration et supervision des phases de montage et démontage avec une approche rationnelle et efficace.",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "Analyse \u0026 suivi post-événement",
    description:
      "Débriefing complet, suivi administratif et comptable, analyse des retours terrain et accompagnement dans l'évolution du projet.",
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-8");

          cardsRef.current.forEach((card, i) => {
            if (card) {
              setTimeout(() => {
                card.classList.add("opacity-100", "translate-y-0");
                card.classList.remove("opacity-0", "translate-y-6");
              }, 120 * i);
            }
          });
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-14 md:py-20 relative opacity-0 translate-y-8 transition-all duration-700 bg-gradient-coral"
    >
      {/* Subtle depth overlay for the gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10 pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-[2.6rem] font-playfair font-bold text-white mb-4 md:mb-6 tracking-tight">
            NOS EXPERTISES
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-[1.8]">
            Lalou Maestra s&apos;adapte à chaque projet : coordination complète d&apos;un événement, accompagnement stratégique, renfort opérationnel ou missions ciblées.
            <br className="hidden md:block" />
            Nous intervenons là où vous avez besoin d&apos;expertise, de structure et de créativité.
          </p>
        </div>

        {/* Expertise Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {expertises.map((exp, index) => (
            <div
              key={exp.title}
              ref={(node) => { cardsRef.current[index] = node; }}
              className="group relative bg-white border border-background-200/50 rounded-2xl p-6 md:p-7 opacity-0 translate-y-6 transition-all duration-700 ease-out hover:border-primary-200/30 hover:shadow-[0_12px_40px_rgba(234,81,73,0.05)] hover:-translate-y-2 hover:scale-[1.02] shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
              style={{ willChange: "transform" }}
            >
              {/* Soft coral glow on hover */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                style={{
                  background: "radial-gradient(ellipse at 50% 0%, rgba(234,81,73,0.04) 0%, transparent 60%)",
                }}
              />

              <div className="relative z-10 flex flex-col h-full">
                {/* Icon */}
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110 bg-primary-50 border border-primary-100">
                  <i className={`${exp.icon} w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-lg md:text-xl text-terracotta`} />
                </div>

                {/* Title */}
                <h3 className="text-[1.05rem] md:text-[1.15rem] font-sans font-semibold mb-3 tracking-tight leading-snug text-terracotta">
                  {exp.title}
                </h3>

                {/* Divider */}
                <div className="w-8 h-px bg-primary-200/50 mb-3.5" />

                {/* Description */}
                <p className="text-[0.88rem] md:text-[0.92rem] text-foreground-800 leading-[1.75] flex-grow">
                  {exp.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}